import React, { useState, useEffect } from "react";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  TextField,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  FormControlLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Divider,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  CheckCircle as CheckCircleIcon,
  Mail as MailIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  Science as TestTubeIcon,
  Settings as SettingsIcon,
  ToggleOn as ToggleOnIcon,
  ToggleOff as ToggleOffIcon,
  Check as CheckIcon,
  Refresh as RefreshIcon,
} from "@mui/icons-material";
import { gmailSettingsApi } from "../../../../services/api/invoice/gmail";

const GmailMappingsSubTab = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [gmailTestDialogOpen, setGmailTestDialogOpen] = useState(false);
  const [gmailTestMapping, setGmailTestMapping] = useState(null);
  const [gmailTestInvoiceNumber, setGmailTestInvoiceNumber] = useState("");
  const [gmailTestQueryPreview, setGmailTestQueryPreview] = useState("");
  const [gmailTestResult, setGmailTestResult] = useState(null);
  const [gmailTestDownloadInvoice, setGmailTestDownloadInvoice] =
    useState(false);

  // Gmail Settings State
  const [gmailSettings, setGmailSettings] = useState([]);
  const [gmailSettingsLoading, setGmailSettingsLoading] = useState(false);
  const [gmailFormData, setGmailFormData] = useState({
    order_place: "",
    search_query: "",
    is_special_case: false,
    download_as_invoice: false,
    email_labels: "",
    is_active: true,
  });
  const [editingGmailId, setEditingGmailId] = useState(null);
  const [showGmailCreateForm, setShowGmailCreateForm] = useState(false);

  // Gmail Settings Functions
  const isGmailQueryValid = (query) => {
    return query && query.includes("NUMBER");
  };

  const formatSearchQueryDisplay = (query) => {
    if (!query) return query;
    return query.split("NUMBER").join("NUMBER");
  };
  const fetchGmailSettings = async (forceRefresh = false) => {
    try {
      setGmailSettingsLoading(true);

      const CACHE_KEY = "gmail_mappings_cache";
      const CACHE_DURATION = 300 * 1000; // 300 seconds

      if (!forceRefresh) {
        const cached = localStorage.getItem(CACHE_KEY);
        if (cached) {
          try {
            const { data, timestamp } = JSON.parse(cached);
            if (Date.now() - timestamp < CACHE_DURATION) {
              setGmailSettings(data);
              setGmailSettingsLoading(false);
              return;
            }
          } catch (e) {
            console.error("Error parsing cache", e);
            localStorage.removeItem(CACHE_KEY);
          }
        }
      }

      const response = await gmailSettingsApi.getAllSettings();
      setGmailSettings(response.data);

      localStorage.setItem(
        CACHE_KEY,
        JSON.stringify({
          data: response.data,
          timestamp: Date.now(),
        }),
      );
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to fetch Gmail settings",
      );
    } finally {
      setGmailSettingsLoading(false);
    }
  };

  const resetGmailForm = () => {
    setGmailFormData({
      order_place: "",
      search_query: "",
      is_special_case: false,
      download_as_invoice: false,
      email_labels: "",
      is_active: true,
    });
    setShowGmailCreateForm(false);
    setEditingGmailId(null);
  };

  const handleGmailSubmit = async (e) => {
    e.preventDefault();

    // Validate that search query contains NUMBER
    if (!isGmailQueryValid(gmailFormData.search_query)) {
      setError(
        'Search query must contain "NUMBER" placeholder that will be replaced with the actual invoice number',
      );
      return;
    }

    try {
      if (editingGmailId) {
        await gmailSettingsApi.updateSetting(editingGmailId, gmailFormData);
        setSuccess("Gmail setting updated successfully");
      } else {
        await gmailSettingsApi.createSetting(gmailFormData);
        setSuccess("Gmail setting created successfully");
      }

      await fetchGmailSettings(true);
      resetGmailForm();
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to save Gmail setting",
      );
    }
  };

  const handleGmailEdit = (setting) => {
    setGmailFormData({
      order_place: setting.order_place,
      search_query: setting.search_query,
      is_special_case: setting.is_special_case,
      download_as_invoice: setting.download_as_invoice,
      email_labels: setting.email_labels || "",
      is_active: setting.is_active,
    });
    setEditingGmailId(setting.id);
    setShowGmailCreateForm(true);
  };

  const handleGmailDelete = async (id, orderPlace) => {
    if (
      !window.confirm(
        `Are you sure you want to delete the Gmail setting for "${orderPlace}"?`,
      )
    ) {
      return;
    }

    try {
      await gmailSettingsApi.deleteSetting(id);
      await fetchGmailSettings(true);
      setSuccess("Gmail setting deleted successfully");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to delete Gmail setting",
      );
    }
  };

  const handleGmailToggle = async (id) => {
    try {
      await gmailSettingsApi.toggleSetting(id);
      await fetchGmailSettings(true);
      setSuccess("Gmail setting status updated");
    } catch (err) {
      setError(
        err.response?.data?.detail ||
          err.message ||
          "Failed to toggle Gmail setting",
      );
    }
  };

  const handleGmailTest = (mapping) => {
    setGmailTestMapping(mapping);
    setGmailTestInvoiceNumber("");
    setGmailTestQueryPreview(mapping.search_query);
    setGmailTestDownloadInvoice(false);
    setGmailTestResult(null);
    setGmailTestDialogOpen(true);
  };

  const parseBlobErrorDetail = async (blob) => {
    if (!blob) return null;
    try {
      const text = await blob.text();
      const parsed = JSON.parse(text);
      return parsed.detail || parsed.message || text;
    } catch {
      return null;
    }
  };

  const startGmailTest = async () => {
    if (!gmailTestInvoiceNumber || !gmailTestMapping) {
      setError("Please provide an invoice number and mapping.");
      return;
    }
    setLoading(true);
    setGmailTestResult(null);
    try {
      const response = await gmailSettingsApi.testMapping(
        gmailTestMapping.order_place,
        gmailTestInvoiceNumber,
        gmailTestMapping.search_query,
        gmailTestDownloadInvoice,
      );

      if (gmailTestDownloadInvoice) {
        const contentType = (
          response.headers["content-type"] || ""
        ).toLowerCase();
        const isJson =
          contentType.includes("application/json") ||
          contentType.includes("text/json");

        if (!isJson && response.data instanceof Blob) {
          const disposition = response.headers["content-disposition"] || "";
          const dispositionMatch = disposition.match(
            /filename\*?=(?:UTF-8''|")?([^\";]+)"?/i,
          );
          const headerName = response.headers["x-invoice-filename"];
          let filename =
            headerName ||
            (dispositionMatch?.[1]
              ? decodeURIComponent(dispositionMatch[1])
              : null);

          if (!filename) {
            let ext = ".bin";
            if (contentType.includes("pdf")) ext = ".pdf";
            else if (
              contentType.includes("rfc822") ||
              contentType.includes("eml")
            )
              ext = ".eml";
            else if (contentType.includes("html")) ext = ".html";
            filename = `Invoice_${gmailTestInvoiceNumber}_test${ext}`;
          }

          const blob = new Blob([response.data], {
            type: contentType || "application/octet-stream",
          });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          setGmailTestResult({
            success: true,
            ephemeral_download: true,
            filename,
            message: `Downloaded ${filename} (not persisted on server).`,
            invoice_number: gmailTestInvoiceNumber,
          });
          setSuccess(`Downloaded ${filename}`);
        } else {
          // JSON error / failure payload returned as blob
          const text =
            response.data instanceof Blob
              ? await response.data.text()
              : typeof response.data === "string"
                ? response.data
                : JSON.stringify(response.data);
          let parsed;
          try {
            parsed = JSON.parse(text);
          } catch {
            parsed = { raw: text };
          }
          setGmailTestResult(parsed);
          if (parsed.success === false) {
            setError(parsed.message || "Invoice download failed.");
          } else {
            setSuccess("Gmail test completed.");
          }
        }
      } else {
        setGmailTestResult(response.data);
        setSuccess("Gmail test completed successfully.");
      }
    } catch (err) {
      let detail = err.message || "Gmail test failed.";
      const data = err.response?.data;
      if (data instanceof Blob) {
        detail = (await parseBlobErrorDetail(data)) || detail;
      } else if (typeof data?.detail === "string") {
        detail = data.detail;
      } else if (data?.detail) {
        detail = JSON.stringify(data.detail);
      }
      setError(detail);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (gmailTestMapping && typeof gmailTestMapping.search_query === "string") {
      setGmailTestQueryPreview(
        gmailTestMapping.search_query.replace(
          /NUMBER/g,
          gmailTestInvoiceNumber,
        ),
      );
    } else {
      setGmailTestQueryPreview("");
    }
  }, [gmailTestInvoiceNumber, gmailTestMapping]);

  // Load Gmail settings on component mount
  useEffect(() => {
    fetchGmailSettings();
  }, []);

  return (
    <Box sx={{ p: 2, space: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <MailIcon color="primary" />
          <Typography variant="h4" fontWeight="bold">
            Gmail Mappings
          </Typography>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button
            variant="outlined"
            onClick={() => fetchGmailSettings(true)}
            startIcon={<RefreshIcon />}
            disabled={gmailSettingsLoading}
          >
            Reload
          </Button>
          <Button
            variant="contained"
            color="primary"
            onClick={() => setShowGmailCreateForm(true)}
            startIcon={<AddIcon />}
          >
            Add Mapping
          </Button>
        </Box>
      </Box>

      {/* Description */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
          <SettingsIcon />
          <Box>
            <Typography variant="subtitle2" fontWeight="medium" gutterBottom>
              Gmail Search Mappings
            </Typography>
            <Typography variant="body2">
              Configure Gmail search queries for different order places. Each
              search query must contain{" "}
              <Box
                component="code"
                sx={{
                  backgroundColor: "action.hover",
                  px: 0.5,
                  borderRadius: 0.5,
                }}
              >
                NUMBER
              </Box>{" "}
              which will be replaced with the actual invoice number when
              searching.
            </Typography>
          </Box>
        </Box>
      </Alert>

      {/* Create/Edit Form */}
      {showGmailCreateForm && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                mb: 2,
              }}
            >
              <Typography variant="h6">
                {editingGmailId ? "Edit Gmail Mapping" : "Create Gmail Mapping"}
              </Typography>
              <IconButton onClick={resetGmailForm}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box
              component="form"
              onSubmit={handleGmailSubmit}
              sx={{ display: "flex", flexDirection: "column", gap: 2 }}
            >
              <Grid container spacing={2}>
                {/* Order Place */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Order Place *"
                    value={gmailFormData.order_place}
                    onChange={(e) =>
                      setGmailFormData({
                        ...gmailFormData,
                        order_place: e.target.value,
                      })
                    }
                    placeholder="e.g., Amazon, eBay, Shopify"
                    required
                  />
                </Grid>
              </Grid>

              {/* Search Query */}
              <Box sx={{ position: "relative" }}>
                <TextField
                  fullWidth
                  label="Gmail Search Query *"
                  multiline
                  rows={3}
                  value={gmailFormData.search_query}
                  onChange={(e) =>
                    setGmailFormData({
                      ...gmailFormData,
                      search_query: e.target.value,
                    })
                  }
                  placeholder='e.g., from:noreply@amazon.com subject:"Your order" NUMBER'
                  required
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        borderColor: isGmailQueryValid(
                          gmailFormData.search_query,
                        )
                          ? "success.main"
                          : "grey.400",
                        borderWidth: isGmailQueryValid(
                          gmailFormData.search_query,
                        )
                          ? 2
                          : 1,
                      },
                      "&:hover fieldset": {
                        borderColor: isGmailQueryValid(
                          gmailFormData.search_query,
                        )
                          ? "success.main"
                          : "primary.main",
                      },
                      "&.Mui-focused fieldset": {
                        borderColor: isGmailQueryValid(
                          gmailFormData.search_query,
                        )
                          ? "success.main"
                          : "primary.main",
                      },
                    },
                    "& .MuiInputBase-input": {
                      fontFamily: "monospace",
                    },
                  }}
                  helperText={
                    isGmailQueryValid(gmailFormData.search_query) ? (
                      <span
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "4px",
                          color: "#2e7d32",
                        }}
                      >
                        <CheckIcon sx={{ fontSize: 16 }} />
                        Query contains required "NUMBER" placeholder
                      </span>
                    ) : (
                      <span style={{ color: "#666" }}>
                        Must contain{" "}
                        <code
                          style={{
                            backgroundColor: "#e3f2fd",
                            color: "#1976d2",
                            padding: "2px 4px",
                            borderRadius: "4px",
                            fontWeight: "bold",
                          }}
                        >
                          NUMBER
                        </code>{" "}
                        placeholder
                      </span>
                    )
                  }
                />
                {isGmailQueryValid(gmailFormData.search_query) && (
                  <Box
                    sx={{
                      position: "absolute",
                      top: 8,
                      right: 8,
                      backgroundColor: "success.main",
                      borderRadius: "50%",
                      width: 20,
                      height: 20,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      zIndex: 1,
                    }}
                  >
                    <CheckIcon sx={{ fontSize: 14, color: "white" }} />
                  </Box>
                )}

                {/* Enhanced display showing NUMBER in blue */}
                {gmailFormData.search_query && (
                  <Box
                    sx={{
                      mt: 1,
                      p: 1,
                      backgroundColor: "grey.50",
                      borderRadius: 1,
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", mb: 0.5 }}
                    >
                      Preview:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: "monospace",
                        wordBreak: "break-word",
                      }}
                    >
                      {gmailFormData.search_query
                        .split("NUMBER")
                        .map((part, index, array) => (
                          <React.Fragment key={index}>
                            {part}
                            {index < array.length - 1 && (
                              <Box
                                component="span"
                                sx={{
                                  color: "primary.main",
                                  fontWeight: "bold",
                                  backgroundColor: "primary.light",
                                  px: 0.5,
                                  borderRadius: 0.5,
                                  border: "1px solid",
                                  borderColor: "primary.main",
                                }}
                              >
                                NUMBER
                              </Box>
                            )}
                          </React.Fragment>
                        ))}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Email Labels */}
              <TextField
                fullWidth
                label="Gmail Labels (optional)"
                value={gmailFormData.email_labels}
                onChange={(e) =>
                  setGmailFormData({
                    ...gmailFormData,
                    email_labels: e.target.value,
                  })
                }
                placeholder="e.g., INVOICES, PROCESSED (comma-separated)"
              />

              {/* Toggles */}
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={gmailFormData.is_special_case}
                        onChange={(e) =>
                          setGmailFormData({
                            ...gmailFormData,
                            is_special_case: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Special Case"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={gmailFormData.download_as_invoice}
                        onChange={(e) =>
                          setGmailFormData({
                            ...gmailFormData,
                            download_as_invoice: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Download as Invoice (email → PDF)"
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={gmailFormData.is_active}
                        onChange={(e) =>
                          setGmailFormData({
                            ...gmailFormData,
                            is_active: e.target.checked,
                          })
                        }
                      />
                    }
                    label="Active"
                  />
                </Grid>
              </Grid>

              {/* Form Actions */}
              <Box sx={{ display: "flex", gap: 2, pt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  startIcon={<SaveIcon />}
                >
                  {editingGmailId ? "Update" : "Create"}
                </Button>
                <Button variant="outlined" onClick={resetGmailForm}>
                  Cancel
                </Button>
              </Box>
            </Box>
          </CardContent>
        </Card>
      )}

      {/* Settings List */}
      <Card>
        <CardContent>
          {gmailSettingsLoading ? (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                p: 4,
              }}
            >
              <CircularProgress />
              <Typography sx={{ ml: 2 }}>Loading Gmail settings...</Typography>
            </Box>
          ) : gmailSettings.length === 0 ? (
            <Box sx={{ textAlign: "center", p: 4 }}>
              <MailIcon sx={{ fontSize: 48, color: "text.secondary", mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No Gmail mappings configured
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Create your first Gmail search mapping to get started.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Order Place</TableCell>
                    <TableCell>Search Query</TableCell>
                    <TableCell>Settings</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {gmailSettings.map((setting) => (
                    <TableRow key={setting.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {setting.order_place}
                          </Typography>
                          {setting.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {setting.description}
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{
                            fontFamily: "monospace",
                            maxWidth: 300,
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {setting.search_query}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: "flex",
                            gap: 0.5,
                            flexWrap: "wrap",
                          }}
                        >
                          {setting.is_special_case && (
                            <Chip
                              size="small"
                              label="Special Case"
                              color="warning"
                            />
                          )}
                          {setting.download_as_invoice && (
                            <Chip
                              size="small"
                              label="Download"
                              color="success"
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Tooltip title="Click to toggle status">
                          <Chip
                            size="small"
                            label={setting.is_active ? "Active" : "Inactive"}
                            color={setting.is_active ? "success" : "default"}
                            icon={
                              setting.is_active ? (
                                <CheckCircleIcon />
                              ) : undefined
                            }
                            onClick={() => handleGmailToggle(setting.id)}
                            sx={{ cursor: "pointer" }}
                          />
                        </Tooltip>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="Test mapping">
                            <IconButton
                              size="small"
                              onClick={() => handleGmailTest(setting)}
                              color="primary"
                            >
                              <TestTubeIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleGmailEdit(setting)}
                              color="primary"
                            >
                              <EditIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton
                              size="small"
                              onClick={() =>
                                handleGmailDelete(
                                  setting.id,
                                  setting.order_place,
                                )
                              }
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
      {/* Gmail Test Result Dialog */}
      <Dialog
        open={gmailTestDialogOpen}
        onClose={() => setGmailTestDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Test Gmail Mapping</DialogTitle>
        <DialogContent dividers>
          {gmailTestMapping && (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Order Place
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {gmailTestMapping.order_place}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Original Query
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{ p: 1, backgroundColor: "action.hover" }}
                >
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {gmailTestMapping.search_query}
                  </Typography>
                </Paper>
              </Box>

              <Divider />

              <TextField
                label="Invoice Number"
                value={gmailTestInvoiceNumber}
                onChange={(e) => setGmailTestInvoiceNumber(e.target.value)}
                fullWidth
                size="small"
              />

              <FormControlLabel
                control={
                  <Checkbox
                    checked={gmailTestDownloadInvoice}
                    onChange={(e) =>
                      setGmailTestDownloadInvoice(e.target.checked)
                    }
                  />
                }
                label="Also download invoice file (PDF/EML; deleted after send)"
              />

              <Box>
                <Typography variant="subtitle2" gutterBottom>
                  Query with Invoice Number
                </Typography>
                <Paper
                  variant="outlined"
                  sx={{
                    p: 1,
                    backgroundColor: (theme) => theme.palette.primary[50],
                    borderColor: "primary.main",
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ fontFamily: "monospace", wordBreak: "break-word" }}
                  >
                    {gmailTestQueryPreview}
                  </Typography>
                </Paper>
              </Box>
            </Box>
          )}
          {/* Show Gmail test result as JSON metadata */}
          {gmailTestResult && (
            <Box mt={2}>
              <Typography variant="subtitle2" gutterBottom>
                Test Result
              </Typography>
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  backgroundColor: (theme) => theme.palette.grey[50],
                }}
              >
                <pre
                  style={{
                    fontFamily: "monospace",
                    fontSize: 14,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {JSON.stringify(gmailTestResult, null, 2)}
                </pre>
              </Paper>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setGmailTestDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={startGmailTest}
            variant="contained"
            color="primary"
            disabled={!gmailTestInvoiceNumber || loading}
            startIcon={loading ? <CircularProgress size={16} /> : null}
          >
            {gmailTestDownloadInvoice ? "Test & Download" : "Start Test"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GmailMappingsSubTab;
